import WarehouseAdminProductLocationData, {
    IWarehouseAdminProductLocationData
} from "@/http/data/warehouse-admin-product-location-data";

export interface IWarehouseAdminOrderItemProductData {
    id: number;
    name: string;
    description?: string|null;
    retour: boolean;
    units?: number|null;
    target_price?: number|null;
}

export interface IWarehouseAdminOrderWarehouseStockData {
    product_id?: number;
    last_stock_count?: number;
    last_stock_count_on?: string|null;
    last_stock_count_by?: string|null;
    received_after_stock_count?: number;
    delivered_after_stock_count?: number;
    calculated_stock?: number;
    open_restock?: number;
}

export interface IWarehouseAdminOrderItemData {
    id: number;
    product_id: number;
    requested: number;
    loaded: number|null;
    load_timestamp?: string|null;
    delivered: number|null;
    deliver_timestamp?: string|null;
    returned: number|null;
    return_timestamp?: string|null;
    retoured: number|null;
    retour_timestamp?: string|null;

    product: IWarehouseAdminOrderItemProductData;

    history_90_days?: number;
    history_365_days?: number;
    suggested_monthly?: number;

    min_stock?: number|null;
    max_stock?: number|null;

    last_stock_count?: number;
    last_stock_count_on?: string|null;
    last_stock_count_by?: string|null;

    building_rack?: string|null;
    building_layer?: string|null;

    warehouse_locations?: IWarehouseAdminProductLocationData[];
    warehouse_stock?: IWarehouseAdminOrderWarehouseStockData|null;

    amount_to_return?: number;
    amount_to_retour?: number;

    units_per_package?: number;
    requested_packages?: number;
    requested_loose_units?: number;
}

export class WarehouseAdminOrderWarehouseStockData {
    public product_id: number|null;
    public last_stock_count: number;
    public last_stock_count_on: string|null;
    public last_stock_count_by: string|null;
    public received_after_stock_count: number;
    public delivered_after_stock_count: number;
    public calculated_stock: number;
    public open_restock: number;

    constructor(data: IWarehouseAdminOrderWarehouseStockData) {
        this.product_id = data.product_id === undefined
            ? null
            : Number(data.product_id);
        this.last_stock_count = Number(data.last_stock_count ?? 0);
        this.last_stock_count_on = data.last_stock_count_on ?? null;
        this.last_stock_count_by = data.last_stock_count_by ?? null;
        this.received_after_stock_count = Number(
            data.received_after_stock_count ?? 0
        );
        this.delivered_after_stock_count = Number(
            data.delivered_after_stock_count ?? 0
        );
        this.calculated_stock = Number(data.calculated_stock ?? 0);
        this.open_restock = Number(data.open_restock ?? 0);
    }
}

export default class WarehouseAdminOrderItemData {
    public id: number;
    public product_id: number;
    public requested: number;
    public loaded: number|null;
    public load_timestamp: string|null;
    public delivered: number|null;
    public deliver_timestamp: string|null;
    public returned: number|null;
    public return_timestamp: string|null;
    public retoured: number|null;
    public retour_timestamp: string|null;

    public product: IWarehouseAdminOrderItemProductData;

    public history_90_days: number;
    public history_365_days: number;
    public suggested_monthly: number;

    public min_stock: number|null;
    public max_stock: number|null;

    public last_stock_count: number;
    public last_stock_count_on: string|null;
    public last_stock_count_by: string|null;

    public building_rack: string|null;
    public building_layer: string|null;

    public warehouse_locations: WarehouseAdminProductLocationData[];
    public warehouse_stock: WarehouseAdminOrderWarehouseStockData|null;

    public amount_to_return: number;
    public amount_to_retour: number;

    public units_per_package: number;
    public requested_packages: number;
    public requested_loose_units: number;

    constructor(data: IWarehouseAdminOrderItemData) {
        this.id = Number(data.id);
        this.product_id = Number(data.product_id);
        this.requested = Number(data.requested ?? 0);
        this.loaded = data.loaded === null
            ? null
            : Number(data.loaded);
        this.load_timestamp = data.load_timestamp ?? null;
        this.delivered = data.delivered === null
            ? null
            : Number(data.delivered);
        this.deliver_timestamp = data.deliver_timestamp ?? null;
        this.returned = data.returned === null
            ? null
            : Number(data.returned);
        this.return_timestamp = data.return_timestamp ?? null;
        this.retoured = data.retoured === null
            ? null
            : Number(data.retoured);
        this.retour_timestamp = data.retour_timestamp ?? null;

        this.product = {
            ...data.product,
            id: Number(data.product.id),
            retour: Boolean(data.product.retour),
            units: data.product.units === null
                ? null
                : Number(data.product.units ?? 1),
            target_price: data.product.target_price === null
                ? null
                : Number(data.product.target_price ?? 0)
        };

        this.history_90_days = Number(data.history_90_days ?? 0);
        this.history_365_days = Number(data.history_365_days ?? 0);
        this.suggested_monthly = Number(data.suggested_monthly ?? 0);

        this.min_stock = data.min_stock === null
            ? null
            : Number(data.min_stock ?? 0);
        this.max_stock = data.max_stock === null
            ? null
            : Number(data.max_stock ?? 0);

        this.last_stock_count = Number(data.last_stock_count ?? 0);
        this.last_stock_count_on = data.last_stock_count_on ?? null;
        this.last_stock_count_by = data.last_stock_count_by ?? null;

        this.building_rack = data.building_rack ?? null;
        this.building_layer = data.building_layer ?? null;

        this.warehouse_locations = (data.warehouse_locations ?? []).map(
            location => new WarehouseAdminProductLocationData(location)
        );

        this.warehouse_stock = data.warehouse_stock
            ? new WarehouseAdminOrderWarehouseStockData(data.warehouse_stock)
            : null;

        this.amount_to_return = Number(data.amount_to_return ?? 0);
        this.amount_to_retour = Number(data.amount_to_retour ?? 0);

        this.units_per_package = Math.max(
            1,
            Number(data.units_per_package ?? 1)
        );
        this.requested_packages = Number(data.requested_packages ?? 0);
        this.requested_loose_units = Number(
            data.requested_loose_units ?? 0
        );
    }
}